import { useMutation, useQuery } from '@tanstack/react-query';
import { toast } from 'sonner';
import { downloadUrl, licensingService, type Platform } from '@/services/api/licensing.service';
import { queryKeys } from './keys';

/**
 * The lab's licence and the build it can install.
 *
 * Two queries rather than one because they change on completely different
 * clocks: a licence changes when somebody buys or releases a seat, a published
 * version when engineering ships. Merging them would mean refetching seat data
 * to find out whether there is an update.
 */

export function useLicence() {
  return useQuery({
    queryKey: queryKeys.licences.mine(),
    queryFn: ({ signal }) => licensingService.read(signal),
    /*
     * A minute. Seats are released and activated by people, not by the system,
     * so this changes on human timescales - but a technician who has just
     * released a seat looks straight back at this list.
     */
    staleTime: 60_000,
  });
}

export function useLatestVersion(platform: Platform = 'windows') {
  return useQuery({
    queryKey: queryKeys.software.latest(platform),
    queryFn: ({ signal }) => licensingService.latestVersion(platform, signal),
    // An hour. A release is an event measured in weeks; polling for one is
    // pure noise, and the page is reloaded far more often than we ship.
    staleTime: 60 * 60_000,
  });
}

/**
 * Mints a link and sends the browser to it.
 *
 * `window.location.assign` rather than an anchor: the target answers a 302 to
 * the CDN, and the token does not exist until this mutation returns - so there
 * is no href to put on a link at render time. The navigation does not leave the
 * page, because the response is a file download.
 *
 * Not invalidating anything on success. The download count moves, and nothing
 * on screen shows it.
 */
export function useDownloadInstaller() {
  return useMutation({
    mutationFn: (platform: Platform = 'windows') => licensingService.mintDownload(platform),
    onSuccess: (link) => {
      window.location.assign(downloadUrl(link));
    },
    onError: () => {
      toast.error('Could not start the download', {
        description: 'Try again in a moment, or raise an issue if it keeps failing.',
      });
    },
  });
}

/**
 * Reveals the caller's own licence key. A mutation, never a query: the key must
 * not land in the query cache, where it would outlive the 60 seconds it is
 * meant to be on screen and survive into devtools.
 */
export function useRevealLicenceKey() {
  return useMutation({
    mutationFn: (password?: string) => licensingService.revealKey(password),
  });
}
